import React from 'react';
import { Link } from 'react-router-dom';
import { Cable } from 'lucide-react';
import { fechaCorta, plural } from 'lib/auditoria';
import { medida } from 'lib/arboles';
import { rutaLinea, textoAnios, tipoEventoLabel, tramo } from 'lib/cables';
import { ResultadoDiscreto } from 'components/ResultadoCable';
import Bloque, { Cifras } from './Bloque';

export function vidaRestante(l) {
  const n = l.vida_restante_anios;
  if (n === null || n === undefined) return l.fecha_instalacion ? 'Sin vida útil registrada' : 'Sin fecha de instalación';
  return l.cambio_vencido ? `Vencida hace ${textoAnios(n)}` : textoAnios(n);
}

function Anual({ l, compacto = false }) {
  if (!l.anual) return <ResultadoDiscreto resultado={null} />;
  return (
    <span className={compacto ? 'inline' : 'block'}>
      <ResultadoDiscreto resultado={l.anual.resultado} />
      <span className={`${compacto ? 'ml-1' : 'block'} text-xs text-muted-foreground`}>
        {fechaCorta(l.anual.fecha)}{l.anualEnPeriodo ? '' : ', antes del período'}
      </span>
    </span>
  );
}

export function textoCables(r) {
  if (!r.cables) return 'No hay cables registrados en esta instalación.';
  return `${plural(r.conEventosPeriodo, 'cable con eventos', 'cables con eventos')} en el período, de ${r.cables}.`;
}

export default function BloqueCables({ control, error }) {
  const r = control?.resumen;
  return (
    <Bloque titulo="Cables" icono={Cable} testId="bloque-cables"
      resumen={control ? textoCables(r) : 'No se pudo consultar los cables.'}
      cifras={control && (
        <Cifras items={[
          { label: 'Cables', valor: r.cables },
          { label: 'Con eventos en el período', valor: r.conEventosPeriodo },
          { label: 'Eventos en el período', valor: r.eventosPeriodo },
          { label: 'Inspección anual en el período', valor: r.anualEnPeriodo },
          { label: 'Vida útil vencida', valor: r.vidaVencida },
        ]} />
      )}>
      {!control ? (
        <p className="text-sm text-muted-foreground">{error || 'No se pudo consultar los cables.'} El reporte lo indicará.</p>
      ) : control.filas.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay cables registrados en esta instalación.</p>
      ) : (
        <>
          {!control.eventosDisponibles && (
            <p className="mb-2 text-xs text-muted-foreground">No se pudo consultar los eventos: los del período no se cuentan.</p>
          )}
          {r.sinAnualEnPeriodo > 0 && control.eventosDisponibles && (
            <p className="mb-2 text-xs text-muted-foreground" data-testid="obs-anual">
              {r.sinAnualEnPeriodo === r.cables
                ? 'Sin inspección anual registrada en el período.'
                : `${r.sinAnualEnPeriodo} de ${r.cables} cables sin inspección anual registrada en el período.`}
            </p>
          )}
          <table className="hidden w-full text-sm sm:table" data-testid="tabla-cables">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">Cable</th>
                <th className="py-1.5 pr-3 font-medium">Tramo</th>
                <th className="py-1.5 pr-3 text-right font-medium">Longitud</th>
                <th className="py-1.5 pr-3 font-medium">Último evento</th>
                <th className="py-1.5 pr-3 text-right font-medium">En el período</th>
                <th className="py-1.5 pr-3 font-medium">Inspección anual</th>
                <th className="py-1.5 font-medium">Vida útil restante</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {control.filas.map((l) => (
                <tr key={l.id} className="align-top">
                  <td className="py-2 pr-3">
                    <Link to={rutaLinea(l.id)} className="font-semibold text-primary underline-offset-2 hover:underline">{l.codigo}</Link>
                    {l.nombre && <span className="block text-xs text-muted-foreground">{l.nombre}</span>}
                  </td>
                  <td className="py-2 pr-3 text-foreground">{tramo(l) || <span className="text-muted-foreground">Sin dato</span>}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-foreground">
                    {medida(l.longitud_m, 'm') || <span className="text-muted-foreground">Sin dato</span>}
                  </td>
                  <td className="py-2 pr-3 text-foreground">
                    {l.ultimo_evento_fecha ? (
                      <>
                        <span className="whitespace-nowrap">{fechaCorta(l.ultimo_evento_fecha)}</span>
                        <span className="block text-xs text-muted-foreground">{tipoEventoLabel(l.ultimo_evento_tipo)}</span>
                      </>
                    ) : <span className="text-muted-foreground">Sin eventos</span>}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-foreground">{l.nPeriodo}</td>
                  <td className="py-2 pr-3"><Anual l={l} /></td>
                  <td className={`py-2 ${l.cambio_vencido ? 'text-error' : l.sinFechaInstalacion ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {vidaRestante(l)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-border sm:hidden" aria-label="Cables">
            {control.filas.map((l) => (
              <li key={l.id}>
                <Link to={rutaLinea(l.id)} className="block py-2.5">
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-primary">{l.codigo}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{l.nombre || tramo(l)}</span>
                    {medida(l.longitud_m, 'm') && <span className="text-xs tabular-nums text-muted-foreground">{medida(l.longitud_m, 'm')}</span>}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    <Anual l={l} compacto />
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {l.ultimo_evento_fecha
                      ? `Último evento: ${fechaCorta(l.ultimo_evento_fecha)}, ${tipoEventoLabel(l.ultimo_evento_tipo).toLowerCase()}`
                      : 'Sin eventos'}
                    {`. ${plural(l.nPeriodo, 'evento', 'eventos')} en el período. Vida útil: ${vidaRestante(l).toLowerCase()}`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Bloque>
  );
}
